import { Show, createSignal, type Component } from 'solid-js';

import { buildId } from './build';
import { TitleBackdrop, TitleMark } from './TitleArt';
import { TitleOptions } from './TitleOptions';
import { EmberField } from './EmberField';

/** What the codex teaches. Only bindings that actually do something are listed. */
const CONTROLS: readonly { key: string; text: string }[] = [
  { key: 'Left click', text: 'Walk to a point on the ground.' },
  { key: 'Click a foe', text: 'Lock on and strike while you are within reach.' },
  { key: 'I', text: 'Open your bag.' },
  { key: 'Drag', text: 'Move an item between bag, vault and paper doll.' },
  { key: 'Esc', text: 'Close whatever is open.' },
];

export interface TitleScreenProps {
  /**
   * Goes on to signing in and picking a character.
   *
   * **The title screen comes first now**, before the gate and before Babylon exists — so it
   * takes no store. Its only previous dependency was the tick rate, which was in a footer
   * that told a player nothing they could act on.
   */
  onEnter: () => void;
}

/**
 * The main menu, and a real gate: nothing connects to the server until ENTER is
 * pressed. The 3D viewport is already rendering behind this — the screen is a
 * stack of scrims over it, so a faint lit plane shows through the dark.
 */
export const TitleScreen: Component<TitleScreenProps> = (props) => {
  const [codexOpen, setCodexOpen] = createSignal(false);
  const [optionsOpen, setOptionsOpen] = createSignal(false);

  return (
    <div class="title" data-testid="title-screen">
      {/* Behind every scrim and in front of the canvas. Shared with the sign-in gate —
          see `TitleArt.tsx` for why it is a component rather than two copies of a
          `srcset`, an overscan, a filter and a drift. */}
      <TitleBackdrop />

      <EmberField />

      <div class="title__body">
        <div class="title__brand">
          {/* The heading survives as the heading; the mark is its text. A screen
              reader still reads a name, and if the file is missing the alt text is
              what the page shows. */}
          {/* The heading survives as the heading; the mark is its text. */}
          <h1 class="title__mark">
            <TitleMark />
          </h1>
          <p class="title__tagline">Die well. Rise sharper.</p>
          <p class="title__epitaph">
            A world that ends every season, and remembers nothing but what your death was worth.
          </p>
        </div>

        <div class="title__menu">
          <button
            type="button"
            class="sigil-button title__enter hud-interactive"
            data-testid="enter-button"
            onClick={() => props.onEnter()}
          >
            <span>Enter the Grind</span>
          </button>

          <div class="title__secondary">
            {/* Options here as well as in the escape menu, because the title screen is now
                the *first* thing a player sees — and turning the music down is the one
                setting somebody reaches for before they have started playing. */}
            <button
              type="button"
              class="sigil-button hud-interactive"
              data-testid="title-options"
              onClick={() => setOptionsOpen(true)}
            >
              <span>Options</span>
            </button>
            <button
              type="button"
              class="sigil-button hud-interactive"
              data-testid="codex-button"
              onClick={() => setCodexOpen(true)}
            >
              <span>Codex</span>
            </button>
          </div>
        </div>
      </div>

      {/* **The build, and only the build.**

          The footer here used to read *Milestone I — Core Combat · 30 Hz Server Authority* and was
          deleted for a good reason: a player has no use for the tick rate, and a milestone number
          says the game is unfinished without saying anything they can act on.

          A build identifier is the opposite kind of fact. It is the one string a player needs when
          something goes wrong — *"ALPHA 202608123 does this"* is a report that can be acted on, and
          "the current version" is not. So it is here, at the smallest size the type scale allows, in
          a corner rather than under the wordmark: findable when looked for and invisible otherwise.

          `DEV` on a local build. See `ui/build.ts` for why that is not a fabricated number. */}
      <p class="title__build" data-testid="build-id">
        {buildId()}
      </p>

      <Show when={optionsOpen()}>
        <TitleOptions onClose={() => setOptionsOpen(false)} />
      </Show>

      <Show when={codexOpen()}>
        <div class="codex" data-testid="codex-panel">
          <div class="plate plate--riveted codex__panel hud-interactive">
            <h2 class="codex__title">The Codex</h2>
            <p class="codex__lede">What little there is to know, so far.</p>
            <hr class="rule" />

            <ul class="codex__list">
              {CONTROLS.map((c) => (
                <li>
                  <kbd class="codex__key">{c.key}</kbd>
                  <span>{c.text}</span>
                </li>
              ))}
            </ul>

            <p class="codex__note">
              Every step, every blow and every death is decided by the server. Your client only
              sends the wish and draws the answer — so what you see is what happened, not what you
              hoped for.
            </p>

            <button
              type="button"
              class="sigil-button codex__close"
              data-testid="codex-close"
              onClick={() => setCodexOpen(false)}
            >
              <span>Close</span>
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};
